import React, { useState, useRef, useEffect } from 'react';
import { Search, ChevronDown, X, Loader2 } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Input } from './input';
import { Button } from './button';

export interface FlexibleTypeAheadOption {
  id: string | number;
  name: string;
}

export interface FlexibleTypeAheadProps {
  value: string;
  onChange: (value: string) => void;
  onSelect: (option: FlexibleTypeAheadOption) => void;
  options?: FlexibleTypeAheadOption[]; // For static options
  searchHook?: (query: string) => { // For API search
    data?: FlexibleTypeAheadOption[];
    isLoading: boolean;
    error: any;
  };
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  clearable?: boolean;
  onClear?: () => void;
  allowAdd?: boolean;
  onAdd?: (value: string) => Promise<FlexibleTypeAheadOption | null>;
  addText?: string;
  minSearchLength?: number;
  debounceMs?: number;
}

const FlexibleTypeAhead: React.FC<FlexibleTypeAheadProps> = ({
  value,
  onChange,
  onSelect,
  options = [],
  searchHook,
  placeholder = "Type to search...",
  disabled = false,
  className,
  clearable = true,
  onClear,
  allowAdd = false,
  onAdd,
  addText = "Add new",
  minSearchLength = 1,
  debounceMs = 300,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState<FlexibleTypeAheadOption[]>(options);
  const [isAdding, setIsAdding] = useState(false);
  const [debouncedValue, setDebouncedValue] = useState(value);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // Debounce the search value
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [value, debounceMs]);

  // Use search hook if provided, otherwise use static options
  const { data: searchResults = [], isLoading, error } = searchHook ? searchHook(debouncedValue) : { data: [], isLoading: false, error: null };

  // Filter static options based on debounced input value
  useEffect(() => {
    if (!searchHook && options.length > 0) {
      if (!debouncedValue.trim()) {
        setFilteredOptions(options);
      } else {
        const filtered = options.filter(option =>
          option.name.toLowerCase().includes(debouncedValue.toLowerCase())
        );
        setFilteredOptions(filtered);
      }
    }
  }, [debouncedValue, options, searchHook]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !inputRef.current?.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);
    setIsOpen(true);

    // Handle N/A case when typing directly
    const naValues = ['N/A', 'NA', 'na', 'n/a'];
    if (naValues.includes(newValue.trim())) {
      const naOption = { id: 0, name: newValue.trim() };
      onSelect(naOption);
    }
  };

  const handleOptionSelect = (option: FlexibleTypeAheadOption) => {
    // Handle N/A case - set ID to 0 for N/A values
    const naValues = ['N/A', 'NA', 'na', 'n/a'];
    const isNA = naValues.includes(option.name);
    const processedOption = isNA ? { ...option, id: 0 } : option;

    onSelect(processedOption);
    setIsOpen(false);
  };

  const handleClear = () => {
    onChange('');
    onClear?.();
    setIsOpen(false);
  };

  const handleAddNew = async () => {
    if (value.trim() && onAdd) {
      try {
        setIsAdding(true);
        const newOption = await onAdd(value.trim());
        if (newOption) {
          // Auto-select the newly created option
          onSelect(newOption);
          setIsOpen(false);
        } else {
          console.error('Failed to create new option');
        }
      } catch (error) {
        console.error('Add error:', error);
      } finally {
        setIsAdding(false);
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      setHighlightedIndex(-1);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!isOpen) {
        setIsOpen(true);
      }
      const availableOptions = searchHook ? searchResults : filteredOptions;
      const totalOptions = availableOptions.length + (allowAdd && value.trim() && !availableOptions.some(opt => opt.name.toLowerCase() === value.toLowerCase()) ? 1 : 0);
      setHighlightedIndex(prev => (prev < totalOptions - 1 ? prev + 1 : prev));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(prev => (prev > 0 ? prev - 1 : -1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightedIndex >= 0) {
        const availableOptions = searchHook ? searchResults : filteredOptions;
        const totalOptions = availableOptions.length;
        if (highlightedIndex < totalOptions) {
          handleOptionSelect(availableOptions[highlightedIndex]);
        } else if (allowAdd && value.trim()) {
          handleAddNew();
        }
      } else if (isOpen) {
        // If dropdown is open but nothing highlighted, select first option or add new
        const availableOptions = searchHook ? searchResults : filteredOptions;
        if (availableOptions.length > 0) {
          handleOptionSelect(availableOptions[0]);
        } else if (allowAdd && value.trim()) {
          handleAddNew();
        }
      }
    }
  };

  // Reset highlighted index when options change
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [searchResults, filteredOptions, value]);

  // Scroll highlighted option into view
  useEffect(() => {
    if (highlightedIndex >= 0 && optionRefs.current[highlightedIndex]) {
      optionRefs.current[highlightedIndex]?.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
      });
    }
  }, [highlightedIndex]);

  // Determine which options to show
  const displayOptions = [...(searchHook ? searchResults : filteredOptions), { id: 0, name: 'N/A' }];
  const showDropdown = isOpen && (displayOptions.length > 0 || isLoading || (value.length >= minSearchLength && displayOptions.length === 0 && !isLoading));

  return (
    <div className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
        <Input
          ref={inputRef}
          value={value}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          className={cn("pl-10 pr-10", className)}
        />
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center space-x-1">
          {isLoading && (
            <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
          )}
          {clearable && value && !isLoading && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleClear}
              className="h-6 w-6 p-0 hover:bg-gray-300 text-gray-400 hover:text-gray-600"
            >
              <X className="h-3 w-3 text-gray-400" />
            </Button>
          )}
          <ChevronDown className="h-4 w-4 text-gray-400" />
        </div>
      </div>

      {showDropdown && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto"
        >
          {isLoading ? (
            <div className="px-4 py-2 text-gray-500 text-sm flex items-center">
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              Searching...
            </div>
          ) : error ? (
            <div className="px-4 py-2 text-red-500 text-sm">
              Error loading results
            </div>
          ) : displayOptions.length > 0 ? (
            <>
              {displayOptions.map((option, index) => {
                const isHighlighted = highlightedIndex === index;
                return (
                  <button
                    key={option.id}
                    ref={(el) => { optionRefs.current[index] = el; }}
                    type="button"
                    onClick={() => handleOptionSelect(option)}
                    className={cn(
                      "w-full px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 text-left focus:outline-none",
                      "text-gray-700 hover:text-gray-900",
                      isHighlighted && "bg-gray-100"
                    )}
                  >
                    {option.name}
                  </button>
                );
              })}
              {allowAdd && value.trim() && !displayOptions.some(opt => opt.name.toLowerCase() === value.toLowerCase()) && (
                (() => {
                  const addIndex = displayOptions.length;
                  const isHighlighted = highlightedIndex === addIndex;
                  return (
                    <button
                      key="add-new"
                      ref={(el) => { optionRefs.current[addIndex] = el; }}
                      type="button"
                      onClick={handleAddNew}
                      disabled={isAdding}
                      className={cn(
                        "w-full px-4 py-2 text-left hover:bg-blue-50 focus:bg-blue-50 focus:outline-none text-blue-600 border-t border-gray-200 disabled:opacity-50",
                        isHighlighted && "bg-blue-50"
                      )}
                    >
                      {isAdding ? 'Adding...' : `${addText}: "${value}"`}
                    </button>
                  );
                })()
              )}
            </>
          ) : (
            <div className="px-4 py-2 text-gray-500 text-sm">
              {value.length < minSearchLength
                ? `Type at least ${minSearchLength} character${minSearchLength > 1 ? 's' : ''} to search`
                : 'No results found'
              }
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FlexibleTypeAhead;
